import { Injectable } from '@angular/core';

import { ApiService } from './api.service';
import { OrderService } from './order.service';
import { RestaurantService } from './restaurant.service';
import { RestaurantLocalStorageService } from './restaurantLocalStorage.service';

/**
 * Cupom v2 (módulo `new_coupon`): cliente dos endpoints /discount-coupons do server.
 * Com a flag ON o /validate é a fonte de verdade das regras (base elegível, teto,
 * produtos que ativam/bloqueiam, partes de pizza, 1º pedido, limite por cliente,
 * disponibilidade), então o front NÃO recalcula desconto: aplica o valor ABSOLUTO
 * resolvido pelo server. Com a flag OFF nada aqui roda e o fluxo legado
 * (CoupomService, /coupom/check) segue intocado.
 */
@Injectable({ providedIn: 'root' })
export class DiscountCouponService {
    // Código escolhido fora da tela de pagamento (pop-up de destaque no cardápio,
    // bottom-sheet de cupons públicos no carrinho ou link /c/<código>). A aplicação de
    // verdade acontece na tela de pagamento, que valida com o contexto completo do pedido.
    // PERSISTIDO em sessionStorage por loja (QA: recarregar o cardápio não pode evaporar
    // o resgate; o cliente via "resgatado" e chegava ao pagamento sem o cupom). Escopo de
    // ABA de propósito, o mesmo do "já visto" do pop-up: morre ao fechar, não vaza entre
    // lojas nem gruda entre visitas. O campo em memória é fallback para storage indisponível.
    private pendingCodeFallback = '';

    // Chegada pelo link compartilhável (/c/<código>): além do pendente, guarda o anúncio
    // que o cardápio mostra UMA vez ("Cupom X ativado!"). Separado do pendingCode para o
    // toast não disparar de novo em revisitas ao cardápio com o cupom ainda pendente.
    // Em memória de propósito: anúncio é da CHEGADA, recarga não deve re-anunciar.
    private linkAnnounceCode = '';

    // O que o SERVER respondeu sobre o Cupom v2 nesta loja, aprendido das proprias chamadas
    // de cupom em vez de lido do cardapio cacheado (CDN + 24h de localStorage).
    // Persistido por loja: um refresh na tela de pagamento nao pode perder o aprendizado e
    // voltar a rotear pela flag velha.
    private static readonly MODULE_FACT_KEY = 'discountCoupon.v2Live';

    private static readonly MODULE_DISABLED = 'MODULE_DISABLED';

    constructor(
        private apiService: ApiService,
        private orderService: OrderService,
        private restaurantService: RestaurantService,
        private restaurantLocalStorage: RestaurantLocalStorageService,
    ) {}

    /**
     * Telefone mais fresco da sessão: o digitado na etapa de entrega (order.user) vence
     * o salvo em pedidos anteriores (localStorage). É o telefone das regras de CLIENTE
     * do /validate (1º pedido / 1 uso por cliente); vazio = regras puladas pelo server.
     */
    get knownPhone(): string {
        const fromOrder = this.orderService && this.orderService.order && this.orderService.order.user ? this.orderService.order.user.phone : '';

        return fromOrder || this.restaurantLocalStorage.getItem('phone') || '';
    }

    /**
     * Mesmo gate do server: a loja precisa ter cupons habilitados (coupoms_active) e o
     * módulo new_coupon ligado. new_coupon OFF = tela/fluxo antigos, nada de v2.
     *
     * A resposta do SERVER vence o cardápio quando existe. As duas leituras vinham do
     * `cardapio.json`, que é servido por CDN e ainda fica 24h no localStorage: ligar o
     * módulo para uma loja não chegava ao cliente, e como este getter também ROTEIA o
     * pagamento entre v2 e legado (payment.page: `checkCoupon`), a flag velha mandava o
     * cupom v2 ser procurado na tabela antiga. Ver `learnFromServer`.
     */
    get isActive(): boolean {
        const fact = this.readModuleFact();

        if (fact !== null) return fact;

        const info = this.restaurantService.restaurant && this.restaurantService.restaurant.info;

        return !!(info && info.coupoms_active && info.modules && info.modules.new_coupon);
    }

    /**
     * Aprende com a resposta das chamadas de cupom, e só quando ela PROVA alguma coisa:
     *
     * - 403 `MODULE_DISABLED`: o middleware do server barrou. Prova que o módulo está OFF.
     * - lista com pelo menos um cupom: o `availableQuery` do server devolve vazio quando
     *   `coupoms_active` está desligado, então um cupom devolvido prova que os DOIS
     *   interruptores estão ligados.
     * - lista vazia com 200: ambíguo (loja sem cupom público x cupons desligados). Não
     *   ensina nada, e o getter segue com o que tiver.
     */
    private learnFromServer(coupons: any[]) {
        if (coupons && coupons.length) this.writeModuleFact(true);
    }

    private learnFromRejection(error) {
        if (error && error.code === DiscountCouponService.MODULE_DISABLED) this.writeModuleFact(false);
    }

    private readModuleFact(): boolean | null {
        const stored = this.restaurantLocalStorage.getItem(DiscountCouponService.MODULE_FACT_KEY);

        if (stored === '1') return true;
        if (stored === '0') return false;

        return null;
    }

    private writeModuleFact(enabled: boolean) {
        try {
            this.restaurantLocalStorage.setItem(DiscountCouponService.MODULE_FACT_KEY, enabled ? '1' : '0');
        } catch (e) {
            // storage indisponível (aba anônima com quota zerada): segue pelo cardápio
        }
    }

    setPendingCode(code: string) {
        this.writePendingCode(code || '');
    }

    get hasPendingCode(): boolean {
        return !!this.readPendingCode();
    }

    peekPendingCode(): string {
        return this.readPendingCode();
    }

    consumePendingCode(): string {
        const code = this.readPendingCode();
        this.writePendingCode('');
        return code;
    }

    private pendingStorageKey(): string {
        const info = this.restaurantService.restaurant && this.restaurantService.restaurant.info;

        return `dc-pending-${(info && info.id) || 'boot'}`;
    }

    private readPendingCode(): string {
        try {
            return sessionStorage.getItem(this.pendingStorageKey()) || '';
        } catch (e) {
            return this.pendingCodeFallback;
        }
    }

    private writePendingCode(code: string) {
        this.pendingCodeFallback = code;
        try {
            if (code) sessionStorage.setItem(this.pendingStorageKey(), code);
            else sessionStorage.removeItem(this.pendingStorageKey());
        } catch (e) { /* storage indisponível: fica só o fallback em memória */ }
    }

    // Entrada do CouponLinkGuard (/c/<código>): deixa pendente E arma o anúncio de chegada.
    setPendingCodeFromLink(code: string) {
        this.setPendingCode(code);
        this.linkAnnounceCode = code || '';
    }

    /** Consome (uma vez) o anúncio de chegada pelo link; vazio se não veio de link. */
    consumeLinkAnnouncement(): string {
        const code = this.linkAnnounceCode;
        this.linkAnnounceCode = '';
        return code;
    }

    // Rejeições que NÃO mudam com o carrinho: manter o pendente só re-mostraria o mesmo
    // erro a cada visita ao pagamento. As demais (MIN_ORDER, PRODUCT_RULE...) são
    // sensíveis ao pedido, e o pendente fica para re-tentar quando o cliente voltar,
    // honrando o "monte seu pedido" prometido na chegada pelo link.
    private static readonly PERMANENT_REJECTIONS = ['NOT_FOUND', 'INACTIVE', 'STORE_DISABLED', 'EXPIRED', 'USAGE_LIMIT', 'PER_CUSTOMER', 'FIRST_ORDER_ONLY'];

    /** Rejeição que NÃO muda com o carrinho (o cupom "caiu"): quem guarda pendente descarta. */
    static isPermanentRejection(errorCode: string): boolean {
        return DiscountCouponService.PERMANENT_REJECTIONS.indexOf(errorCode) !== -1;
    }

    /**
     * Chamado no catch do /validate: decide o destino do pendente após uma rejeição.
     * Só mexe se o código rejeitado É o pendente (comparação no espaço canônico), para a
     * rejeição de um código digitado à mão não matar a escolha do pop-up/link.
     */
    settlePendingAfterRejection(rejectedCode: string, errorCode: string) {
        const pending = this.readPendingCode();
        if (!pending) return;

        const canonical = (value: string) => (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (canonical(rejectedCode) !== canonical(pending)) return;

        if (DiscountCouponService.isPermanentRejection(errorCode)) {
            this.writePendingCode('');
        }
    }

    // Cupons marcados como públicos na nova tela (bottom-sheet do carrinho). Erro aqui
    // não pode quebrar o carrinho: resolve com lista vazia.
    /**
     * REJEITA em falha, não resolve com []: lista vazia e lista que não deu para buscar são
     * coisas diferentes para quem chama. Tratadas igual, um soluço de rede fazia o banner de
     * cupons sumir do carrinho como se a loja não tivesse cupom público nenhum.
     */
    listPublic(): Promise<any[]> {
        return new Promise((resolve, reject) => {
            this.apiService.get(`/restaurant/${this.restaurantId()}/discount-coupons/public`).subscribe(
                (response) => {
                    const coupons = (response && response.data) || [];
                    this.learnFromServer(coupons);
                    resolve(coupons);
                },
                (error) => {
                    this.learnFromRejection(error);
                    reject(error);
                },
            );
        });
    }

    // Cupom em destaque (pop-up do cardápio). O server garante no máximo 1 por loja.
    getFeatured(): Promise<any> {
        return new Promise((resolve) => {
            this.apiService.get(`/restaurant/${this.restaurantId()}/discount-coupons/featured`).subscribe(
                (response) => {
                    const coupons = (response && response.data) || [];
                    this.learnFromServer(coupons);
                    resolve(coupons[0] || null);
                },
                (error) => {
                    this.learnFromRejection(error);
                    resolve(null); // pop-up não tem estado a perder: degrada em silêncio
                },
            );
        });
    }

    /**
     * Valida o código contra o carrinho. Resolve com o shape do server
     * ({ valid, couponId, code, discountType, discountValue }) — discountValue já é o
     * valor ABSOLUTO (free_shipping devolve a taxa informada em deliveryFee). Rejeita
     * com o body do 422 ({ valid: false, code: ERRO, message }).
     */
    validate(code: string, cart: { subtotal: number; deliveryFee?: number; deliveryType?: string; items?: any[]; paymentMethod?: any }): Promise<any> {
        return new Promise((resolve, reject) => {
            this.apiService
                .post(`/restaurant/${this.restaurantId()}/discount-coupons/validate`, {
                    code: code,
                    phone: this.knownPhone || null,
                    cart: {
                        subtotal: cart.subtotal,
                        deliveryFee: cart.deliveryFee != null ? cart.deliveryFee : null,
                        orderType: this.mapOrderType(cart.deliveryType),
                        items: cart.items || [],
                        // Forma de pagamento quando o cliente JA escolheu (tela de pagamento):
                        // { paymentMethodKey, restaurantCardTypeId?, cardTypeName? }. Sem
                        // escolha, null, e a regra fica para o placement (gate final).
                        paymentMethod: cart.paymentMethod || null,
                    },
                })
                .subscribe(
                    (response) => resolve((response && response.data) || response),
                    (error) => reject(error),
                );
        });
    }

    /**
     * Itens do carrinho (shoppingCartService.products) → contrato de items do /validate:
     * { productId, lineTotal, featured, isPizzaCombo, pizza }. O preço da linha vem de
     * quem conhece o cálculo (shoppingCartService.itemSubtotal), este service não
     * recalcula dinheiro.
     */
    mapItems(products: any[], lineTotalOf: (index: number) => number): any[] {
        return (products || []).map((product, index) => {
            const item: any = {
                productId: product && product.type === 'general' && product.id != null ? Number(product.id) : null,
                lineTotal: Number(lineTotalOf(index)) || 0,
                featured: !!(product && product.featured),
            };

            if (product && product.type === 'pizza') {
                item.pizza = this.pizzaRef(product);
            }

            // Combo de pizza: comboItems são os tamanhos; a regra de bloquear/ativar por
            // parte precisa enxergar dentro (mesmo merge que o server faz no placement).
            if (product && product.type === 'combo' && Array.isArray(product.comboItems) && product.comboItems.length) {
                item.isPizzaCombo = true;
                item.pizza = this.mergePizzaRefs(product.comboItems.map((size) => this.pizzaRef(size)));
            }

            return item;
        });
    }

    // orders.delivery_type do app → ENUM do v2 (delivery/pickup/table). Desconhecido ou
    // ainda não escolhido vira null: regra de tipo não-verificável NÃO rejeita (mesmo
    // comportamento do placement no server).
    mapOrderType(deliveryType?: string): string {
        switch (deliveryType) {
            case 'delivery':
                return 'delivery';
            case 'table':
                return 'table';
            case 'balcony':
            case 'car_takeout':
            case 'pickup':
                return 'pickup';
        }

        return null;
    }

    // Mensagens pt-BR por errorCode do Enforcement (o `message` do server é em inglês).
    static errorMessage(error: any): string {
        const messages = {
            NOT_FOUND: 'Cupom inválido. Verifique se está correto ou ainda disponível.',
            INACTIVE: 'Este cupom não está mais ativo.',
            STORE_DISABLED: 'Os cupons desta loja estão desativados no momento.',
            EXPIRED: 'Este cupom expirou.',
            UNAVAILABLE_TIME: 'Este cupom não está disponível neste dia/horário.',
            USAGE_LIMIT: 'Este cupom atingiu o limite de usos.',
            PER_CUSTOMER: 'Você já usou este cupom.',
            FIRST_ORDER_ONLY: 'Este cupom é válido apenas para o primeiro pedido.',
            MIN_ORDER: 'O pedido não atingiu o valor mínimo deste cupom.',
            ORDER_TYPE: 'Este cupom não vale para este tipo de pedido.',
            FREE_SHIPPING_NO_FEE: 'O frete deste pedido já está grátis, o cupom não é necessário.',
            PAYMENT_METHOD: 'Este cupom não vale para a forma de pagamento escolhida.',
            FEATURED_ITEMS: 'Este cupom não pode ser aplicado em produtos em destaque.',
            PRODUCT_RULE: 'Este cupom não vale para os produtos do seu carrinho.',
            PIZZA_COMBO: 'Este cupom não vale para combos de pizza.',
        };

        return (error && messages[error.code]) || 'Não foi possível aplicar o cupom.';
    }

    private pizzaRef(item: any) {
        // Pós-addProduct o item pizza tem flavors.selected; antes (carrinho recém-montado)
        // ainda é selectedFlavors. Os dois carregam o mesmo shape.
        const flavors = (item && ((item.flavors && item.flavors.selected) || item.selectedFlavors)) || [];
        const extraIds = [];

        flavors.forEach((flavor) => {
            ((flavor && flavor.additionalToppings) || []).forEach((topping) => {
                if (topping && topping.id != null) extraIds.push(Number(topping.id));
            });
        });

        return {
            sizeId: item && item.id != null ? Number(item.id) : null,
            flavorIds: flavors.filter((flavor) => flavor && flavor.id != null).map((flavor) => Number(flavor.id)),
            crustId: item && item.crust && item.crust.id != null ? Number(item.crust.id) : null,
            doughId: item && item.dough && item.dough.id != null ? Number(item.dough.id) : null,
            extraIds: extraIds,
        };
    }

    private mergePizzaRefs(refs: any[]) {
        const merged = { sizeIds: [], flavorIds: [], crustIds: [], doughIds: [], extraIds: [] };

        refs.forEach((ref) => {
            if (ref.sizeId != null) merged.sizeIds.push(ref.sizeId);
            if (ref.crustId != null) merged.crustIds.push(ref.crustId);
            if (ref.doughId != null) merged.doughIds.push(ref.doughId);
            merged.flavorIds = merged.flavorIds.concat(ref.flavorIds);
            merged.extraIds = merged.extraIds.concat(ref.extraIds);
        });

        return merged;
    }

    private restaurantId(): number {
        return this.restaurantService.restaurant.info.id;
    }
}
